import { useState, memo, type ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  Trash2, Plus, Eye, EyeOff,
  LogIn, Flag, LayoutDashboard, CreditCard, Search, TextCursorInput, Settings, Menu,
} from 'lucide-react'
import { POLISH_PASS_TYPES, bugCategoryLabel, type PolishPassType } from '../../netlify/functions/lib/polish-pass-types'
import { resolveLoginMode, loginModeGlobalVariable, type LoginMode, type LoginChoice } from '../../netlify/functions/lib/login-mode'
// Re-export the login-mode helpers (defined server-side in netlify/functions/lib so v1.ts can use them
// too) so the onboarding UIs can keep importing everything login-related from qaConfig.
export { resolveLoginMode, loginModeGlobalVariable, type LoginMode, type LoginChoice }
// Shared QA-configuration controls (Test scope, Polish passes, Logins) used by both the new-project
// flow and the onboarding chat. The np-* styling lives in StartQaForm.css
// — the single home of this design system (those classes are used nowhere else).
import './StartQaForm.css'

export const DEFAULT_EXPLORATION_PROMPT = 'Explore the app and test the main features'

// Exhaustive over PolishPassType, so a new pass cannot ship with a blank description in onboarding.
export const PASS_DESCRIPTIONS: Record<PolishPassType, string> = {
  'network-performance': 'Detect slow requests, large payloads, and waterfall issues',
  'react-rendering': 'Find unnecessary re-renders and performance bottlenecks',
  'layout-shift': 'Identify elements that shift unexpectedly during load',
  accessibility: 'Check for WCAG violations, including text color contrast',
  glitches: 'Spot visual glitches, flickers, and rendering artifacts',
  'user-experience': 'Analyze interactions for usability improvements',
  seo: 'Check titles, descriptions, canonicals, robots directives, and crawlable navigation',
  'ui-details': 'Examine recordings for complex UI features needing detailed testing',
  security: 'Probe for exposed secrets, unsafe requests, and missing access controls',
}

export const SCOPE_AREAS: { id: string; label: string; Icon: LucideIcon }[] = [
  { id: 'auth', label: 'Authentication', Icon: LogIn },
  { id: 'onboarding', label: 'Onboarding', Icon: Flag },
  { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { id: 'checkout', label: 'Checkout & payments', Icon: CreditCard },
  { id: 'search', label: 'Search & filters', Icon: Search },
  { id: 'forms', label: 'Forms & inputs', Icon: TextCursorInput },
  { id: 'settings', label: 'Settings', Icon: Settings },
  { id: 'nav', label: 'Navigation', Icon: Menu },
]

export interface LoginEntry {
  email: string
  password: string
}

export type ScopeMode = 'all' | 'focus'

// A small styled checkbox-as-toggle. Shared so the same control renders identically everywhere.
export const ToggleField = memo(function ToggleField({
  checked,
  onChange,
  disabled,
  label,
  testId,
  analyticsEvent,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  label: ReactNode
  testId?: string
  analyticsEvent?: string
}) {
  return (
    <label className="np-toggle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        data-testid={testId}
        data-analytics-event={analyticsEvent}
      />
      <span>{label}</span>
    </label>
  )
})

// === Polish passes ===

export const PolishPassesSelector = memo(function PolishPassesSelector({
  enabled,
  onToggle,
  disabled,
}: {
  enabled: Set<string>
  onToggle: (passType: string) => void
  disabled?: boolean
}) {
  return (
    <div className="np-passes">
      {POLISH_PASS_TYPES.map((passType) => (
        <div key={passType} className="np-pass" data-testid={`polish-pass-${passType}`}>
          <label className="np-toggle np-toggle--top">
            <input
              type="checkbox"
              checked={enabled.has(passType)}
              onChange={() => onToggle(passType)}
              disabled={disabled}
              data-analytics-event="dashboard-start-qa-polish-pass-toggle"
            />
            <span className="np-pass__txt">
              <span className="np-pass__name">{bugCategoryLabel(passType)}</span>
              <span className="np-pass__desc">{PASS_DESCRIPTIONS[passType]}</span>
            </span>
          </label>
        </div>
      ))}
    </div>
  )
})

// === Logins ===

export const LoginsEditor = memo(function LoginsEditor({
  logins,
  onLoginsChange,
  autoCreateAccount,
  onAutoCreateChange,
  errors,
  disabled,
  // When set, password fields are masked with a per-row show/hide button.
  maskPassword,
  // When set, the "Let agents register their own test accounts" toggle is not rendered.
  hideAutoCreate,
}: {
  logins: LoginEntry[]
  onLoginsChange: (logins: LoginEntry[]) => void
  autoCreateAccount: boolean
  onAutoCreateChange: (value: boolean) => void
  errors?: Record<string, string>
  disabled?: boolean
  maskPassword?: boolean
  hideAutoCreate?: boolean
}) {
  const [revealed, setRevealed] = useState<Set<number>>(() => new Set())
  const toggleRevealed = (index: number) =>
    setRevealed((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  const update = (index: number, field: keyof LoginEntry, value: string) =>
    onLoginsChange(logins.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)))
  const remove = (index: number) => onLoginsChange(logins.filter((_, i) => i !== index))
  const add = () => onLoginsChange([...logins, { email: '', password: '' }])

  return (
    <>
      <div className="np-logins">
        {logins.map((loginEntry, i) => (
          <div key={i} className="np-login-row" data-testid={`login-entry-${i}`}>
            <div className="np-login-field">
              <input
                type="text"
                className="np-input"
                value={loginEntry.email}
                onChange={(e) => update(i, 'email', e.target.value)}
                placeholder="email or username"
                disabled={disabled}
                aria-label={`Login ${i + 1} email`}
                data-testid={`login-email-${i}`}
                data-analytics-event="dashboard-start-qa-login-email-input"
              />
              {errors?.[`login_${i}_email`] && (
                <span className="np-field-error" role="alert">{errors[`login_${i}_email`]}</span>
              )}
            </div>
            <div className={`np-login-field${maskPassword ? ' np-login-field--password' : ''}`}>
              <input
                type={maskPassword && !revealed.has(i) ? 'password' : 'text'}
                className="np-input"
                value={loginEntry.password}
                onChange={(e) => update(i, 'password', e.target.value)}
                placeholder="password"
                disabled={disabled}
                aria-label={`Login ${i + 1} password`}
                data-testid={`login-password-${i}`}
                data-analytics-event="dashboard-start-qa-login-password-input"
              />
              {maskPassword && (
                <button
                  type="button"
                  className="np-login-reveal"
                  onClick={() => toggleRevealed(i)}
                  disabled={disabled}
                  aria-label={revealed.has(i) ? `Hide login ${i + 1} password` : `Show login ${i + 1} password`}
                  aria-pressed={revealed.has(i)}
                  data-testid={`login-reveal-${i}`}
                  data-analytics-event="qa-config-login-reveal"
                >
                  {revealed.has(i) ? <EyeOff size={15} strokeWidth={1.75} aria-hidden /> : <Eye size={15} strokeWidth={1.75} aria-hidden />}
                </button>
              )}
              {errors?.[`login_${i}_password`] && (
                <span className="np-field-error" role="alert">{errors[`login_${i}_password`]}</span>
              )}
            </div>
            <button
              type="button"
              className="np-icon-btn"
              onClick={() => remove(i)}
              aria-label={`Remove login ${i + 1}`}
              disabled={disabled}
              data-testid={`login-remove-${i}`}
              data-analytics-event="dashboard-start-qa-login-remove"
            >
              <Trash2 size={15} strokeWidth={1.75} aria-hidden />
            </button>
          </div>
        ))}
        <button type="button" className="np-add-row" onClick={add} disabled={disabled} data-testid="add-login-btn" data-analytics-event="dashboard-start-qa-login-add">
          <Plus size={14} strokeWidth={1.75} aria-hidden /> Add credential
        </button>
      </div>
      {!hideAutoCreate && (
        <div className="np-login-note">
          <ToggleField
            checked={autoCreateAccount}
            onChange={onAutoCreateChange}
            disabled={disabled}
            label="Let agents register their own test accounts"
            testId="auto-create-toggle"
            analyticsEvent="dashboard-start-qa-auto-create-toggle"
          />
        </div>
      )}
    </>
  )
})

// === Shared helpers (so both pages derive identical create-project payloads) ===

/** Validate login rows: each non-empty row needs both an email/username and a password. */
export function validateLogins(logins: LoginEntry[]): Record<string, string> {
  const errors: Record<string, string> = {}
  for (let i = 0; i < logins.length; i++) {
    const entry = logins[i]!
    if (!entry.email.trim() && !entry.password.trim()) continue
    if (!entry.email.trim()) errors[`login_${i}_email`] = 'Email/username is required'
    if (!entry.password.trim()) errors[`login_${i}_password`] = 'Password is required'
  }
  return errors
}

export { buildLoginGlobalVariables } from './projectLoginVariables'

/** Build the exploration prompt from the scope selection + login policy. */
export function buildScopeExplorationPrompt(opts: {
  scopeMode: ScopeMode
  scopeAreas: string[]
  instructions: string
  autoCreateAccount: boolean
  logins: LoginEntry[]
}): string {
  // Only honor the free-text instructions in focus mode. In "explore everything" mode a draft may
  // still linger in state (we keep it so switching back to focus restores it), but it must never
  // influence a whole-app run.
  const text = opts.scopeMode === 'focus' ? opts.instructions.trim() : ''
  let prompt: string
  if (opts.scopeMode === 'focus' && text) {
    // In focus mode the free-text box describes WHAT to focus on — used instead of the preset area
    // chips, appended to the default prompt rather than replacing it.
    prompt = `${DEFAULT_EXPLORATION_PROMPT}\n\nFocus your exploration and testing on the following: ${text}`
  } else {
    prompt = text || DEFAULT_EXPLORATION_PROMPT
    if (opts.scopeMode === 'focus' && opts.scopeAreas.length > 0) {
      const labels = SCOPE_AREAS.filter((a) => opts.scopeAreas.includes(a.id)).map((a) => a.label)
      prompt += `\n\nFocus your exploration and testing on these areas of the app: ${labels.join(', ')}.`
    }
  }
  if (!opts.autoCreateAccount) {
    const validLogins = opts.logins.filter((l) => l.email.trim() && l.password.trim())
    return validLogins.length > 0
      ? `${prompt}\n\nIMPORTANT: Do NOT create new accounts. Use only the provided login credentials (available as global variables).`
      : `${prompt}\n\nIMPORTANT: Do NOT create new accounts. The app does not support self-registration.`
  }
  return prompt
}
